import { useSourceDetail } from '../hooks/useSourceDetail';
import { SOURCE_TYPE_LABELS } from '../types/sources.types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { formatDateLong } from '@/shared/utils/format-date.utils';

interface SourceDetailModalProps {
  sourceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatDate(dateString?: string | null): string {
  if (!dateString) return '-';
  return formatDateLong(new Date(dateString));
}

function formatInterval(hours: number): string {
  if (hours < 24) return `Cada ${hours} horas`;
  const days = hours / 24;
  return days === 1 ? 'Cada 1 día' : `Cada ${days} días`;
}

function formatMetric(value: number | null | undefined): string {
  if (value === null || value === undefined) return 'Sin datos disponibles';
  return value.toLocaleString('es-MX');
}

function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return 'Sin datos disponibles';
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.round(ms)}ms`;
}

function formatCoverage(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return 'Sin datos disponibles';
  return `${Math.round(pct)}%`;
}

export function SourceDetailModal({ sourceId, open, onOpenChange }: SourceDetailModalProps) {
  const { source, isLoading } = useSourceDetail(open ? sourceId : undefined);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="source-detail-modal"
        className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Detalle de la fuente</DialogTitle>
        </DialogHeader>

        {isLoading && <div className="py-8" aria-hidden />}

        {source && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Nombre de la fuente
                </label>
                <p data-testid="detail-source-name" className="text-primary font-medium">
                  {source.name}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Tipo de fuente
                </label>
                <p data-testid="detail-source-type" className="text-primary">
                  {SOURCE_TYPE_LABELS[source.type] ?? source.type}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  URL / Endpoint
                </label>
                <p
                  data-testid="detail-source-url"
                  className="text-primary font-mono text-sm break-all"
                >
                  {source.maskedUrl || '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Tipo de credencial
                </label>
                <p data-testid="detail-source-credential-type" className="text-primary">
                  {source.credentialType || '-'}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Estado de credenciales
                </label>
                <p data-testid="detail-source-credential-status" className="text-primary">
                  {source.hasCredentials ? 'Configuradas' : 'No configuradas'}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Intervalo de actualización
                </label>
                <p data-testid="detail-source-interval" className="text-primary">
                  {formatInterval(source.updateIntervalHours)}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">Estado</label>
                <div className="mt-1">
                  <span data-testid="detail-source-status">
                    <StatusBadge
                      status={source.isEnabled ? 'active' : 'inactive'}
                      label={source.isEnabled ? 'Habilitado' : 'Deshabilitado'}
                    />
                  </span>
                </div>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Fecha de creación
                </label>
                <p data-testid="detail-source-created-at" className="text-primary text-sm">
                  {formatDate(source.createdAt)}
                </p>
              </div>
              <div>
                <label className="text-xs text-primary/60 uppercase tracking-wide">
                  Última sincronización
                </label>
                <p data-testid="detail-source-last-sync" className="text-primary text-sm">
                  {formatDate(source.lastSyncAt)}
                </p>
              </div>
            </div>

            <div>
              <label className="text-xs text-primary/60 uppercase tracking-wide mb-3 block">
                Regiones asociadas
              </label>
              {source.regions && source.regions.length > 0 ? (
                <div data-testid="source-regions-list" className="flex flex-wrap gap-2">
                  {source.regions.map((region) => (
                    <span
                      key={region.id}
                      className="text-primary text-sm bg-primary-background px-3 py-1.5 rounded-md"
                    >
                      {region.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p data-testid="source-no-regions" className="text-primary text-sm">
                  No hay regiones asociadas a esta fuente.
                </p>
              )}
            </div>

            <div>
              <label className="text-xs text-primary/60 uppercase tracking-wide mb-3 block">
                Métricas operativas
              </label>
              <div className="grid grid-cols-3 gap-4">
                <div className="border border-outline rounded-lg p-3 bg-background">
                  <label className="text-xs text-primary/60 uppercase tracking-wide block mb-1">
                    Latencia
                  </label>
                  <p
                    data-testid="detail-source-latency"
                    className="text-primary font-medium text-lg"
                  >
                    {formatLatency(source.latencyMs)}
                  </p>
                </div>
                <div className="border border-outline rounded-lg p-3 bg-background">
                  <label className="text-xs text-primary/60 uppercase tracking-wide block mb-1">
                    Volumen
                  </label>
                  <p
                    data-testid="detail-source-volume"
                    className="text-primary font-medium text-lg"
                  >
                    {formatMetric(source.volume)}
                  </p>
                </div>
                <div className="border border-outline rounded-lg p-3 bg-background">
                  <label className="text-xs text-primary/60 uppercase tracking-wide block mb-1">
                    Cobertura
                  </label>
                  <p
                    data-testid="detail-source-coverage"
                    className="text-primary font-medium text-lg"
                  >
                    {formatCoverage(source.coveragePercentage)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-4">
          <UnifiedButton
            data-testid="close-source-detail-modal-btn"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cerrar
          </UnifiedButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
